import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { Input } from '@/components/ui/input';
import { PIECE_NAME_MAX_LENGTH } from '@obraplan/core/domain/takeoff/pile';
import { paraNumero, paraTexto } from './formato';

/** O que o servidor aceita num nome de peça — o campo não deixa passar (o domínio também). */
export const NOME_DA_PECA_MAX = PIECE_NAME_MAX_LENGTH;

/**
 * Os dois campos que o quantitativo repete em toda tela.
 *
 * Ambos existem pelo mesmo motivo: o valor que eles mostram é **calculado** — a medida
 * sai das paredes, o nome sai do par de ambientes —, e campo controlado por valor
 * calculado briga com quem está digitando. Enquanto o foco está no campo, vale o texto;
 * ao sair, volta a valer o que o rascunho guardou, que é o momento certo de discordar.
 */

/**
 * Campo numérico que aceita vírgula.
 *
 * Guarda o **texto** enquanto está em foco, e não o número: um campo controlado pelo valor
 * apaga a vírgula no instante em que ela é digitada — `"3,"` vira `3` vira `"3"` — e a
 * pessoa não consegue escrever 3,5 de jeito nenhum. Ao sair do foco, volta a exibir o
 * número como o sistema o entendeu, que é o momento certo de discordar.
 */
export function NumeroInput({
  id,
  value,
  onChange,
  className,
  ...props
}: {
  id?: string;
  value: number;
  onChange: (value: number) => void;
  className?: string;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'id' | 'className'>) {
  const [texto, setTexto] = useState(() => paraTexto(value));
  const [focado, setFocado] = useState(false);

  useEffect(() => {
    if (!focado) setTexto(paraTexto(value));
  }, [value, focado]);

  return (
    <Input
      {...props}
      {...(id === undefined ? {} : { id })}
      inputMode="decimal"
      value={texto}
      className={className}
      onFocus={() => setFocado(true)}
      onBlur={() => {
        setFocado(false);
        setTexto(paraTexto(value));
      }}
      onChange={(event) => {
        setTexto(event.target.value);
        const numero = paraNumero(event.target.value);
        if (numero !== undefined) onChange(numero);
      }}
    />
  );
}

/**
 * Campo de nome que aceita ficar vazio enquanto se digita.
 *
 * Mesmo motivo do campo numérico: o valor vem calculado — apagar tudo devolveria o nome
 * automático na hora, e a pessoa passaria a escrever **depois** dele. Enquanto está em
 * foco vale o texto; ao sair, volta a mostrar o que o rascunho guardou.
 *
 * ## `onChange` e `onCommit` são coisas diferentes, e a distinção custou caro
 *
 * `onChange` sai **a cada tecla**. Serve para quem escuta é o rascunho do navegador — o
 * nome da parede, que se redesenha na hora e não custa nada.
 *
 * `onCommit` sai **uma vez, ao terminar** — ao sair do campo ou no Enter. Serve para quem
 * escuta é o servidor. O nome da tipologia usava `onChange`, e cada tecla virava uma
 * requisição, uma transação, uma linha de auditoria — que é *append-only* e não volta
 * atrás (RN-112) —, um aviso de sucesso e uma rebusca de tudo o que a tela lê. Escrever
 * "Apartamento Tipo 1" gravava dezoito nomes, dos quais dezessete nunca existiram; e o
 * caminho até "Apartamento 10" passa por "Apartamento 1", que pode já estar em uso — a
 * recusa aparecia no meio da digitação, por causa de um nome que ninguém quis.
 *
 * Com `onCommit`, o que foi digitado **fica** até a gravação responder: quem sai do campo
 * já viu o nome novo, e devolvê-lo ao antigo por um instante pareceria que não pegou.
 * Sem ele, o valor volta a valer ao sair, que continua sendo o certo para o calculado.
 */
export function NomeInput({
  value,
  onChange,
  onCommit,
  respostaImediata = false,
  onDescartar,
  className,
  ...props
}: {
  value: string;
  /** A cada tecla — para quem ouve é o rascunho. */
  onChange?: (value: string) => void;
  /** Ao terminar de escrever — para quem ouve é o servidor. */
  onCommit?: (value: string) => void;
  /**
   * Quem ouve o `onCommit` responde **na mesma hora** — o reducer do rascunho, que aceita e
   * troca o valor, ou recusa e o deixa como está. Recusado, o campo volta ao nome vigente em
   * vez de ficar mostrando o texto que não valeu; com o servidor (resposta tardia), o
   * texto digitado fica até a resposta, como descrito acima.
   */
  respostaImediata?: boolean;
  /** O campo foi esvaziado e voltou ao nome vigente sem gravar nada. */
  onDescartar?: () => void;
  className?: string;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'className'>) {
  const [texto, setTexto] = useState(value);
  const [focado, setFocado] = useState(false);
  /**
   * O último valor que veio de fora.
   *
   * Sincronizar por "não está em foco" bastava enquanto tudo era rascunho, porque a
   * resposta era imediata. Com `onCommit` existe um intervalo — entre sair do campo e a
   * gravação voltar — em que o valor de fora ainda é o antigo, e ressincronizar nele
   * desfaria na tela o que acabou de ser mandado gravar.
   */
  const ultimoDeFora = useRef(value);

  useEffect(() => {
    if (value === ultimoDeFora.current) return;
    ultimoDeFora.current = value;
    if (!focado) setTexto(value);
  }, [value, focado]);

  /** O que acabou de ser mandado gravar, à espera de conferir se o dono o aceitou. */
  const [conferir, setConferir] = useState<string | undefined>(undefined);

  // Roda no quadro em que o dono respondeu: se o valor de fora não virou o que foi mandado, a
  // recusa não deixa o texto recusado na tela.
  useEffect(() => {
    if (conferir === undefined) return;
    setConferir(undefined);
    if (value !== conferir) setTexto(value);
  }, [conferir, value]);

  const encerrar = (): void => {
    setFocado(false);
    const limpo = texto.trim();
    // Nada a gravar — campo esvaziado, nome inalterado, ou valor calculado: o de fora
    // volta a valer, que é o momento certo de discordar de quem digitou.
    if (onCommit === undefined || limpo === '' || limpo === value) {
      setTexto(value);
      if (limpo === '') onDescartar?.();
    } else {
      onCommit(limpo);
      if (respostaImediata) setConferir(limpo);
    }
  };

  return (
    <Input
      maxLength={NOME_DA_PECA_MAX}
      {...props}
      value={texto}
      className={className}
      onFocus={() => setFocado(true)}
      // Encadeia em vez de substituir: quem usa o campo costuma ter o que fazer ao sair
      // dele — fechar a edição do cartão, por exemplo —, e sobrescrever o manipulador
      // deixaria o cartão preso em modo de edição para sempre.
      onBlur={(event) => {
        encerrar();
        props.onBlur?.(event);
      }}
      onKeyDown={(event) => {
        // Enter grava sem exigir que a pessoa procure onde clicar para sair do campo.
        if (event.key === 'Enter') event.currentTarget.blur();
        props.onKeyDown?.(event);
      }}
      onChange={(event) => {
        setTexto(event.target.value);
        onChange?.(event.target.value);
      }}
    />
  );
}

/**
 * Campo numérico que aceita ficar **vazio**.
 *
 * Vazio não é zero, e a diferença é a mesma nos dois lugares onde este campo aparece: o
 * serviço sem espessura não mede em m³ nenhum, enquanto o de espessura zero mediria zero
 * metro cúbico; o item sem quantidade lançada **segue a planilha**, enquanto o item com
 * zero foi zerado por alguém. Tratar as duas como a mesma coisa apagaria uma decisão.
 */
export function NumeroOpcionalInput({
  value,
  onChange,
  className,
  ...props
}: {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  className?: string;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'className'>) {
  const [texto, setTexto] = useState(() => (value === undefined ? '' : paraTexto(value)));
  const [focado, setFocado] = useState(false);

  useEffect(() => {
    if (!focado) setTexto(value === undefined ? '' : paraTexto(value));
  }, [value, focado]);

  return (
    <Input
      {...props}
      inputMode="decimal"
      value={texto}
      className={className}
      onFocus={() => setFocado(true)}
      onBlur={() => {
        setFocado(false);
        setTexto(value === undefined ? '' : paraTexto(value));
      }}
      onChange={(event) => {
        setTexto(event.target.value);
        if (event.target.value.trim() === '') onChange(undefined);
        else {
          const numero = paraNumero(event.target.value);
          if (numero !== undefined) onChange(numero);
        }
      }}
    />
  );
}
